import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ProjectDetail } from '@/lib/content'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useInView } from '@/hooks/use-in-view'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { Reveal } from '@/components/generals/reveal'
import { ConstructionTimeline } from '@/components/projects/construction-timeline'
import { DrawingSet } from '@/components/projects/drawing-set'
import { ProjectHero } from '@/components/projects/project-hero'
import { ScanVsDesign } from '@/components/projects/scan-vs-design'
import { LineChart } from '@/components/charts/line-chart'
import { Treemap } from '@/components/charts/treemap'
import { getProject, getProjectSlugs } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import { groupDigits, monthLabels, pad2, shapeKey } from '@/lib/format'

interface ProjectPageProps {
    detail: ProjectDetail
}

const formatEnergy = (value: number): string => value.toFixed(1)

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const ProjectPage: FC<ProjectPageProps> = ({ detail }): ReactNode => {
    const { project, number, total, previous, next } = detail
    const inProgress = project.status.trim().toLowerCase() === 'in progress'
    const series = project.energySeries
    const labels = monthLabels(project.monitoringStart || '2024-10-01', Math.max(24, series.length))
    const readings = series.filter((value): value is number => value !== null)
    const lastTwelve = readings.slice(-12)
    const annual = lastTwelve.length === 12 ? lastTwelve.reduce((sum, value) => sum + value, 0) : null
    const underTarget = readings.filter(value => value <= project.energyTarget).length
    const firstReading = series.findIndex(value => value !== null)
    const [materialsRef, materialsInView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })

    const lessons = [
        { column: 'lesson_one', text: project.lessonOne },
        { column: 'lesson_two', text: project.lessonTwo },
        { column: 'lesson_three', text: project.lessonThree }
    ].filter(lesson => lesson.text)

    return (
        <Layout>
            <Seo
                title={project.name}
                description={project.summary || `${project.name}, ${project.city}: a live point-cloud model by Norrvik.`}
                figure={`Fig. 01 / Project ${number} of ${String(total).padStart(2, '0')} / ${project.city}`}
                shape={shapeKey(project.shape, project.slug)}
            />

            <ProjectHero project={project} number={number} total={total} />

            <dl className='grid grid-cols-2 border-b border-hairline desk:grid-cols-4'>
                <div className='flex min-h-[96px] flex-col justify-center gap-1 border-b border-r border-hairline py-5 pl-gutter pr-4 desk:border-b-0'>
                    <dt className='hud text-mute'>
                        Area
                    </dt>

                    <dd className='font-heading text-[22px] font-medium tracking-[-0.02em] text-ink'>
                        <span>
                            {groupDigits(project.areaM2)}
                        </span>

                        <span className='text-dim'>
                            {' '}m²
                        </span>
                    </dd>
                </div>

                <div className='flex min-h-[96px] flex-col justify-center gap-1 border-b border-hairline px-4 py-5 desk:border-b-0 desk:border-r'>
                    <dt className='hud text-mute'>
                        Year
                    </dt>

                    <dd className='font-heading text-[22px] font-medium tracking-[-0.02em] text-ink'>
                        {project.year}
                    </dd>
                </div>

                <div className='flex min-h-[96px] flex-col justify-center gap-1 border-r border-hairline py-5 pl-gutter pr-4 desk:pl-4'>
                    <dt className='hud text-mute'>
                        Structure
                    </dt>

                    <dd className='font-heading text-[22px] font-medium tracking-[-0.02em] text-ink' data-mdlfy-cms={`projects:${project.id}:structural_system`}>
                        {project.structuralSystem}
                    </dd>
                </div>

                <div className='flex min-h-[96px] flex-col justify-center gap-1 py-5 pl-4 pr-gutter'>
                    <dt className='hud text-mute'>
                        Status
                    </dt>

                    <dd className='flex items-center gap-3 font-heading text-[22px] font-medium tracking-[-0.02em]'>
                        <span aria-hidden='true' className={inProgress ? 'inline-block size-2 bg-signal' : 'inline-block size-2 bg-point'} />

                        <span className={inProgress ? 'text-signal' : 'text-ink'} data-mdlfy-cms={`projects:${project.id}:status`}>
                            {project.status}
                        </span>
                    </dd>
                </div>
            </dl>

            <section aria-labelledby='drawings-title' className='border-b border-hairline'>
                <Reveal className='grid gap-8 px-gutter pb-10 pt-20 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:pt-28'>
                    <div>
                        <FigLabel fig='02' title='Drawing set' detail='Plan, section, elevation, axonometric' note='Fine lines drawn from the model / point cloud on the last tab' />

                        <h2 id='drawings-title' className='display-md mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink'>
                                    Drawn from
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(90)}>
                                    the same model.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(200)}>
                        Every drawing below comes from the model the building was built and scanned against, with its grid, levels and the dimensions the site team worked to.
                    </p>
                </Reveal>

                <div className='pb-16 desk:pb-20'>
                    <DrawingSet shape={shapeKey(project.shape, project.slug)} name={project.name} inProgress={inProgress} />
                </div>
            </section>

            <section aria-labelledby='deviation-title' className='border-b border-hairline'>
                <Reveal className='grid gap-8 px-gutter pb-10 pt-20 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:pt-28'>
                    <div>
                        <FigLabel fig='03' title='Scan vs design' detail='As built against as drawn' note='Grey under 3 mm / blue over 8 mm' />

                        <h2 id='deviation-title' className='display-md mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink'>
                                    The building,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(90)}>
                                    against its drawing.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(200)}>
                        Drag the handle to compare the design with the latest scan. Each point is coloured by how far the built surface sits from the drawn one.
                    </p>
                </Reveal>

                <div className='px-gutter pb-16 desk:pb-20'>
                    <ScanVsDesign
                        projectId={project.id}
                        shape={shapeKey(project.shape, project.slug)}
                        name={project.name}
                        deviationMm={project.deviationMm}
                        deviationNote={project.deviationNote}
                    />
                </div>
            </section>

            <section aria-labelledby='construction-title' className='border-b border-hairline'>
                <Reveal className='px-gutter pb-10 pt-20 desk:pt-28'>
                    <FigLabel fig='04' title='Construction' detail='Survey to handover' note={inProgress ? 'Later milestones are upcoming' : 'All milestones complete'} />

                    <h2 id='construction-title' className='sr-only'>
                        Construction timeline
                    </h2>
                </Reveal>

                <div className='px-gutter pb-16 desk:pb-20'>
                    <ConstructionTimeline milestones={project.construction} />
                </div>
            </section>

            <section aria-labelledby='credits-title' className='border-b border-hairline'>
                <Reveal className='px-gutter pb-10 pt-20 desk:pt-28'>
                    <FigLabel fig='05' title='Credits' detail='Team' note='Everyone who built it' />

                    <h2 id='credits-title' className='sr-only'>
                        Credits
                    </h2>
                </Reveal>

                <Reveal>
                    <dl className='grid gap-px border-t border-hairline bg-hairline sm:grid-cols-2 desk:grid-cols-5'>
                        {[
                            { label: 'Client', value: project.client, column: 'client' },
                            { label: 'Structure', value: project.creditStructure, column: 'credit_structure' },
                            { label: 'Services', value: project.creditServices, column: 'credit_services' },
                            { label: 'Contractor', value: project.creditContractor, column: 'credit_contractor' },
                            { label: 'Scan imagery', value: 'Norrvik', column: null }
                        ].map((credit, index) => (
                            <div key={credit.label} className='reveal-fade flex min-h-[132px] flex-col justify-between gap-6 bg-background px-gutter py-6 desk:px-6 desk:first:pl-gutter desk:last:pr-gutter' style={delay(index * 70)}>
                                <dt className='hud text-mute'>
                                    {credit.label}
                                </dt>

                                <dd className='font-heading text-[20px] font-medium leading-tight tracking-[-0.015em] text-ink'>
                                    {credit.column && credit.value ? (
                                        <span data-mdlfy-cms={`projects:${project.id}:${credit.column}`}>
                                            {credit.value}
                                        </span>
                                    ) : (credit.value || 'To be confirmed')}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Reveal>
            </section>

            <section aria-labelledby='narrative-title' className='border-b border-hairline'>
                <Reveal className='grid gap-10 px-gutter py-20 desk:grid-cols-[minmax(0,0.8fr)_minmax(0,1.7fr)] desk:gap-16 desk:py-28'>
                    <div>
                        <FigLabel fig='06' title='Narrative' detail='What the model found' note='About 200 words' />

                        <h2 id='narrative-title' className='sr-only'>
                            About the project
                        </h2>

                        <dl className='reveal-fade mt-10 border-t border-hairline' style={delay(160)}>
                            {project.client && (
                                <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                    <dt className='hud text-mute'>
                                        Client
                                    </dt>

                                    <dd className='hud text-right text-ink' data-mdlfy-cms={`projects:${project.id}:client`}>
                                        {project.client}
                                    </dd>
                                </div>
                            )}

                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Location
                                </dt>

                                <dd className='hud text-right text-ink'>
                                    <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                        {project.city}
                                    </span>

                                    {project.country && (
                                        <>
                                            <span className='text-mute'>
                                                {' / '}
                                            </span>

                                            <span data-mdlfy-cms={`projects:${project.id}:country`}>
                                                {project.country}
                                            </span>
                                        </>
                                    )}
                                </dd>
                            </div>

                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Sensors
                                </dt>

                                <dd className='hud text-ink'>
                                    {groupDigits(project.sensors)}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className='grid gap-8 desk:grid-cols-2 desk:gap-12'>
                        <p className='reveal-fade text-[17px] leading-[1.6] text-ink desk:text-[18px]' style={delay(120)} data-mdlfy-cms={`projects:${project.id}:narrative_lead`}>
                            {project.narrativeLead}
                        </p>

                        <p className='reveal-fade text-[17px] leading-[1.6] text-ink/80 desk:text-[18px]' style={delay(220)} data-mdlfy-cms={`projects:${project.id}:narrative_body`}>
                            {project.narrativeBody}
                        </p>
                    </div>
                </Reveal>
            </section>

            <section aria-labelledby='energy-title' className='border-b border-hairline'>
                <Reveal className='grid gap-12 px-gutter py-20 desk:grid-cols-[minmax(0,0.8fr)_minmax(0,1.7fr)] desk:gap-16 desk:py-28'>
                    <div>
                        <FigLabel fig='07' title='Energy' detail='24 months monitored' note='kWh per m² per month / dashed line is the target' />

                        <h2 id='energy-title' className='display-md mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink'>
                                    Energy use,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(90)}>
                                    month by month.
                                </span>
                            </span>
                        </h2>

                        <dl className='reveal-fade mt-10 border-t border-hairline' style={delay(200)}>
                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Last 12 months
                                </dt>

                                <dd className='hud text-ink'>
                                    {annual === null ? `${project.energyKwhM2} kWh/m² design` : `${Math.round(annual)} kWh/m²`}
                                </dd>
                            </div>

                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Monthly target
                                </dt>

                                <dd className='hud text-ink'>
                                    {formatEnergy(project.energyTarget)} kWh/m²
                                </dd>
                            </div>

                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Months at or under target
                                </dt>

                                <dd className='hud text-ink'>
                                    {readings.length === 0 ? 'From handover' : `${pad2(underTarget)} / ${pad2(readings.length)}`}
                                </dd>
                            </div>

                            <div className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                <dt className='hud text-mute'>
                                    Embodied carbon
                                </dt>

                                <dd className='hud text-ink'>
                                    {project.carbonKgM2} kgCO2e/m²
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <figure className='reveal-fade min-w-0' style={delay(160)}>
                        <LineChart
                            title={`Monitored energy use for ${project.name}, kWh per square metre per month`}
                            labels={labels}
                            series={[{ name: 'Measured', values: series.length ? series : new Array(labels.length).fill(null) }]}
                            unit='kWh/m²'
                            target={{ value: project.energyTarget, label: `Target ${formatEnergy(project.energyTarget)} kWh/m²` }}
                            targetDashed
                            height={340}
                            emptyMessage='Monitoring begins at handover. The dashed blue line is the design target.'
                        />

                        <figcaption className='hud mt-5 text-mute'>
                            {readings.length > 0
                                ? `Monthly energy use per square metre of floor area, ${labels[Math.max(0, firstReading)]} to ${labels[labels.length - 1]}. The dashed blue line is the design target.`
                                : 'No monitored months yet. Readings appear here from handover onwards.'}
                        </figcaption>
                    </figure>
                </Reveal>
            </section>

            <section aria-label='Materials and lessons' className='grid border-b border-hairline desk:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]'>
                <div ref={materialsRef} data-reveal={materialsInView ? 'in' : 'pre'} className='border-b border-hairline px-gutter py-20 desk:border-b-0 desk:border-r desk:py-24'>
                    <div data-ruler-mark='08'>
                        <p className='hud text-ink'>
                            Fig. 08 / Materials / Structural mass
                        </p>

                        <p className='hud text-mute'>
                            Share by weight, as built
                        </p>
                    </div>

                    {project.materials.length > 0 ? (
                        <Treemap title={`Share of structural mass by material, ${project.name}`} items={project.materials} run={materialsInView} aspect={1.5} className='mt-10' />
                    ) : (
                        <p className='hud mt-10 border border-hairline px-4 py-10 text-center text-mute'>
                            Material quantities are confirmed at handover.
                        </p>
                    )}
                </div>

                <Reveal className='px-gutter py-20 desk:py-24'>
                    <FigLabel fig='09' title='Lessons learned' detail='Carried into the next model' note={`${pad2(lessons.length)} entries`} />

                    <ol className='mt-10 border-t border-hairline'>
                        {lessons.map((lesson, index) => (
                            <li key={lesson.column} className='reveal-fade grid grid-cols-[3.5rem_minmax(0,1fr)] gap-4 border-b border-hairline py-6' style={delay(120 + index * 90)}>
                                <span className='hud text-ink'>
                                    L{pad2(index + 1)}
                                </span>

                                <span className='min-w-0'>
                                    <span className='hud block text-mute'>
                                        Lesson learned
                                    </span>

                                    <span className='hud mt-2 block leading-[1.7] text-ink' data-mdlfy-cms={`projects:${project.id}:${lesson.column}`}>
                                        {lesson.text}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ol>
                </Reveal>
            </section>

            <nav aria-label='More projects' className='grid border-b border-hairline desk:grid-cols-2'>
                <Link href={`/projects/${previous.slug}`} className='group flex flex-col gap-6 border-b border-hairline px-gutter py-12 transition-colors duration-300 hover:bg-panel desk:border-b-0 desk:border-r desk:py-16'>
                    <span className='hud flex items-center gap-2 text-mute'>
                        <ArrowLeft className='size-4 transition-transform duration-500 ease-instrument group-hover:-translate-x-1' strokeWidth={1.5} aria-hidden='true' />
                        Previous project / {previous.number}
                    </span>

                    <span className='display-lg text-dim transition-colors duration-500 group-hover:text-ink group-focus-visible:text-ink' data-mdlfy-cms={`projects:${previous.id}:name`}>
                        {previous.name}
                    </span>
                </Link>

                <Link href={`/projects/${next.slug}`} className='group flex flex-col items-end gap-6 px-gutter py-12 text-right transition-colors duration-300 hover:bg-panel desk:py-16'>
                    <span className='hud flex items-center gap-2 text-mute'>
                        Next project / {next.number}
                        <ArrowRight className='size-4 transition-transform duration-500 ease-instrument group-hover:translate-x-1' strokeWidth={1.5} aria-hidden='true' />
                    </span>

                    <span className='display-lg text-dim transition-colors duration-500 group-hover:text-ink group-focus-visible:text-ink' data-mdlfy-cms={`projects:${next.id}:name`}>
                        {next.name}
                    </span>
                </Link>
            </nav>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getProjectSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Project paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ProjectPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    try {
        const detail = await getProject(slug)

        if (!detail) return { notFound: true, revalidate: 60 }

        return { props: { detail }, revalidate: 60 }
    }
    catch (error) {
        console.error(`Project ${slug} could not be loaded:`, error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { notFound: true, revalidate: 30 }
    }
}

export default ProjectPage
